import Card from '@pelckmans/elementis/components/card';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { ANALYTICS_EVENT_SUBLOCATIONS } from '../../../../../constants/analyticsEvents';
import IdentityContext from '../../../../../contexts/IdentityContext';
import ModalContext from '../../../../../contexts/ModalContext';
import { AssignmentDeadlinePill } from '../../../../../shared-components/AssignmentDeadlinePill';
import ModuleContext from '../../../_contexts/ModuleContext';
import { MaterialIcon } from '../../../components/MaterialIcon';
import useSlideSets from '../../../hooks/useSlideSets';
import MedialinksContext from '../../_contexts/MedialinksContext';
import type { PinnedMaterial } from '../../_contexts/PinnedMaterialContext';
import TableOfContentsContext from '../../_contexts/TableOfContentsContext';
import UserMaterialContext from '../../_contexts/UserMaterialContext';
import useMedialinks from '../../hooks/medialink/useMedialinks';
import useUserMaterial from '../../hooks/user-material/useUserMaterial';
import MaterialPills from '../MaterialPills';
import ScorableProgress from '../ScorableProgress';
import SharedMaterialsContext from '../../_contexts/SharedMaterialsContext';
import { SharedUserMaterial } from '../../../../../api/user-material';

export default function PinnedMaterialCard({ material }: { material: PinnedMaterial }) {
  const { t } = useTranslation();
  const { identity } = useContext(IdentityContext);
  const { module, selectedModule } = useContext(ModuleContext);
  const { selectedNode } = useContext(TableOfContentsContext);
  const { showModal } = useContext(ModalContext);

  const { medialinks } = useContext(MedialinksContext);
  const { openMedialink } = useMedialinks();

  const { userMaterial, fetchUserMaterial } = useContext(UserMaterialContext);
  const { sharedMaterials } = useContext(SharedMaterialsContext);
  const { openMaterial: openUserMaterial } = useUserMaterial();

  const { buildDoceoUrl, captureOpenSlideSetEvents } = useSlideSets();

  function getIconText() {
    if (material.deadline && !identity.isTeacherAlike) {
      return t('PAGES.TOC_LEVEL.PINNED_MATERIAL.SUBTITLE.ASSIGNMENT');
    } else if (material.type === 'user-material') {
      return identity.isTeacherAlike
        ? t('PAGES.TOC_LEVEL.PINNED_MATERIAL.SUBTITLE.OWN_MATERIAL')
        : t('PAGES.TOC_LEVEL.PINNED_MATERIAL.SUBTITLE.SHARED_USER_MATERIAL');
    } else if (material.type === 'slide-set') {
      return t('PAGES.TOC_LEVEL.PINNED_MATERIAL.SUBTITLE.SLIDE_SET');
    } else {
      return material.contentType?.name;
    }
  }

  function getTags() {
    if (identity.isTeacherAlike) {
      return {
        'aria-label': t('PAGES.TOC_LEVEL.MATERIAL_PILLS.A11Y_LABEL'),
        role: 'group',
        children: (
          <MaterialPills
            isAssignment={material.isAssignment}
            isShared={material.isShared}
            isStudent={material.group === 'student'}
            isTeacher={material.group === 'teacher'}
          />
        ),
      };
    }
    if (material.deadline) {
      return {
        'aria-label': t('SHARED_COMPONENTS.ASSIGNMENT_DEADLINE_PILL.A11Y_LABEL'),
        role: 'group',
        children: <AssignmentDeadlinePill deadline={material.deadline} />,
      };
    }
  }

  if (material.type === 'slide-set') {
    return (
      <Card
        titleText={material.name}
        titleTag="h3"
        as="link"
        icon={<MaterialIcon materialType="slide-set" isUserSlideSet={material.isUserSlideSet} />}
        iconText={getIconText()}
        tags={getTags()}
        href={buildDoceoUrl({ ...material, id: Number(material.id) })}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => captureOpenSlideSetEvents(Number(material.id), 'portaal-pinned-material')}
      />
    );
  }

  async function handleClickMaterial() {
    if (!selectedNode || !selectedModule || !module) return;

    if (material.type === 'medialink') {
      const medialink = medialinks.find(x => x.id === material.id);

      if (medialink) {
        const modal = await openMedialink(
          medialink,
          selectedNode,
          module,
          selectedModule,
          undefined,
          undefined,
          ANALYTICS_EVENT_SUBLOCATIONS.PORTAAL_PINNED_MATERIAL,
        );

        if (modal) {
          showModal(modal);
        }
      }
    }

    if (material.type === 'user-material') {
      const foundUserMaterial = userMaterial.find(x => x.id === material.id);
      const foundSharedMaterial = sharedMaterials.find(x => x.id === material.id) as SharedUserMaterial;
      const foundMaterial = foundUserMaterial ?? foundSharedMaterial;

      if (foundMaterial) {
        const modal = await openUserMaterial(
          foundMaterial,
          foundMaterial.userMaxScore ? fetchUserMaterial : undefined,
          ANALYTICS_EVENT_SUBLOCATIONS.PORTAAL_PINNED_MATERIAL,
        );
        if (modal) showModal(modal);
      }
    }
  }

  return (
    <Card
      as="button"
      titleText={material.name}
      titleTag="h3"
      onClick={handleClickMaterial}
      icon={<MaterialIcon materialType={material.type} contentType={material.contentType} />}
      iconText={getIconText()}
      tags={getTags()}
      results={material.score && <ScorableProgress score={material.score} />}
    />
  );
}
